import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Send } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { pushNotifications } from '../../utils/pushNotifications';

export function NotificationsSettings({ setError, setSuccess }) {
  const { language } = useLanguage();
  const pushSupported = pushNotifications.isSupported();
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  useEffect(() => {
    pushNotifications.isSubscribed().then(setPushEnabled);
  }, []);

  const handleTogglePush = async () => {
    setPushLoading(true);
    setError('');
    setSuccess('');
    try {
      if (pushEnabled) {
        await pushNotifications.unsubscribe();
        setPushEnabled(false);
        setSuccess(language === 'es' ? 'Notificaciones desactivadas' : 'Notifications disabled');
      } else {
        await pushNotifications.subscribe();
        setPushEnabled(true);
        setSuccess(language === 'es' ? '¡Notificaciones activadas!' : 'Notifications enabled!');
      }
    } catch (e) {
      if (e.message === 'permission-denied') {
        setError(language === 'es' 
          ? 'Permiso denegado. Habilita las notificaciones en la configuración de tu navegador.' 
          : 'Permission denied. Enable notifications in your browser settings.');
      } else {
        setError(language === 'es' ? 'Error al configurar notificaciones' : 'Error configuring notifications');
      }
    } finally {
      setPushLoading(false);
    }
  };

  const handleTestPush = async () => {
    setPushLoading(true);
    setError('');
    setSuccess('');
    const ok = await pushNotifications.sendTest();
    if (ok) {
      setSuccess(language === 'es' ? 'Notificación de prueba enviada' : 'Test notification sent');
    } else {
      setError(language === 'es' ? 'No se pudo enviar la prueba' : 'Could not send test');
    }
    setPushLoading(false);
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 space-y-6" data-testid="notifications-section">
      <div>
        <h2 className="text-base sm:text-lg font-bold text-gray-800 flex items-center gap-2 mb-2">
          <Bell size={18} className="text-blue-500" />
          {language === 'es' ? 'Notificaciones Push' : 'Push Notifications'}
        </h2>
        <p className="text-xs sm:text-sm text-gray-500">
          {language === 'es' 
            ? 'Recibe avisos del sistema cuando tengas una extracción disponible, incluso con la app cerrada.'
            : 'Receive system alerts when you have an extraction available, even with the app closed.'}
        </p>
      </div>

      {!pushSupported ? (
        <div className="p-3 sm:p-4 bg-amber-50 rounded-xl border border-amber-100 text-sm text-amber-700" data-testid="push-unsupported-msg">
          {language === 'es' 
            ? 'Tu navegador no soporta notificaciones push. En iOS, instala la app en tu pantalla de inicio (Compartir → Agregar a inicio) para habilitarlas.'
            : 'Your browser does not support push notifications. On iOS, install the app to your home screen (Share → Add to Home Screen) to enable them.'}
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-xl border border-gray-100">
            <div className="flex items-center gap-3">
              {pushEnabled ? <Bell size={20} className="text-emerald-500" /> : <BellOff size={20} className="text-gray-400" />}
              <div>
                <p className="font-bold text-gray-800 text-sm">
                  {language === 'es' ? 'Notificaciones del sistema' : 'System notifications'}
                </p>
                <p className="text-xs text-gray-500">
                  {pushEnabled 
                    ? (language === 'es' ? 'Activadas en este dispositivo' : 'Enabled on this device')
                    : (language === 'es' ? 'Desactivadas' : 'Disabled')}
                </p>
              </div>
            </div>
            <button
              onClick={handleTogglePush}
              disabled={pushLoading}
              data-testid="push-toggle-btn"
              className={`relative w-14 h-8 rounded-full transition-colors duration-300 disabled:opacity-50 ${
                pushEnabled ? 'bg-emerald-500' : 'bg-gray-300'
              }`}
            >
              <span className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform duration-300 ${
                pushEnabled ? 'translate-x-7' : 'translate-x-1'
              }`} style={{ left: 0 }} />
            </button>
          </div>

          {pushEnabled && (
            <button
              onClick={handleTestPush}
              disabled={pushLoading}
              data-testid="push-test-btn"
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 disabled:opacity-50 active:scale-95 text-sm"
            >
              <Send size={16} />
              {language === 'es' ? 'Enviar notificación de prueba' : 'Send test notification'}
            </button>
          )}
        </>
      )}
    </div>
  );
}
